// shared-lib/cabinet-runs/applianceInstall.js
//
// An appliance's INSTALL pieces — the fridge surround, the over-fridge
// cabinet, the range hood — are CABINETRY the run builds around an appliance,
// not the appliance itself. `buildApplianceObjects` emits them from the bay's
// footprint, which since #1754 is the appliance object's own size.
//
// `materializeRunObjects` drops a derived run's whole `appliancePlacements`
// list, because the APPLIANCE object on a scan/auto run belongs to whoever
// measured or generated it and must never be re-emitted. That rule was applied
// to the install pieces too, and they are a different thing: swapping a 30"
// fridge for a 50" one left a 31" surround wrapped around it and an
// over-fridge cabinet the taller box grew 15" through — the symptom #1754
// opens with, unrepaired on exactly the runs most kitchens have.
//
// So: split the list. The appliance objects stay with their owner; the install
// pieces are the run's to rebuild — for a GENERATED run only. A scan run's
// surround was MEASURED, and replacing it with a built one would throw away
// the scan's own record of the room. Pure.

const { baysForRun, isApplianceKind } = require('./bays');
const { mayResizeObject } = require('../fixture-types/productRepresentation');
const { isWoodHood } = require('../fixture-types/hoodForm');
const { withoutWoodHood, woodHoodSideProudM } = require('./woodHoods');
const { getWallAxes } = require('../kitchen-assembly/wallMath');
const { resizeDimensions } = require('../geometry/resizeDimensions');
const { applianceObjectFor } = require('./moveAppliances');

/** Fixture-group prefixes that mark a piece as an appliance INSTALL. */
const INSTALL_GROUP_RE = /(?:FridgeInstall|VentHood)_/;

/**
 * Types that are ALWAYS an install piece, whatever group they carry.
 *
 * The group alone is not enough: the kitchen generator gives its range hood
 * the RANGE's own group (`kitchenAppliance_range`) on purpose — "so the hood
 * travels with the range" — so a group test misses it, and the run then
 * emitted a second hood under its own id beside the first (user-found
 * 2026-09-05). The over-fridge cabinet still needs the group, because
 * `upper-cabinet` says nothing on its own.
 */
const INSTALL_TYPES = new Set(['vent-hood', 'fridge-surround']);

/** The ids of the appliance OBJECTS a run's bays claim. */
function applianceObjectIds(run) {
  const ids = new Set();
  for (const b of baysForRun(run)) {
    if (!isApplianceKind(b.kind)) continue;
    if (b.applianceId) ids.add(b.applianceId);
    if (b.id) ids.add(b.id);
  }
  return ids;
}

/**
 * The key an install group is suffixed with, for one appliance. The generator
 * names its groups after the PLACEMENT (`kitchenAppliance_fridge` →
 * `kitchenFridgeInstall_fridge`) while the run names them after the OBJECT
 * (`manualAppliance_<objId>` → `manualFridgeInstall_<objId>`), so an appliance
 * can be reached by either.
 */
function installKeysFor(appliance) {
  const keys = new Set();
  if (appliance?.id) keys.add(appliance.id);
  const g = appliance?.fixtureGroupId;
  const m = g && /^.*?(?:Appliance|FridgeInstall|VentHood)_(.+)$/.exec(g);
  if (m) keys.add(m[1]);
  return keys;
}

/**
 * Is `obj` an install piece belonging to one of `appliances`?
 *
 * Three ways to qualify. `installFor` NAMES its appliance outright and is the
 * one to prefer — a dishwasher end panel is a `blank-panel`, a type that says
 * nothing on its own (an island's back cover is one too), and its id is keyed
 * to its span rather than to the appliance, so neither of the older tests can
 * reach it. Otherwise: an unambiguous install TYPE, or an install GROUP, tied
 * to one of the appliances by the key its id or its group is suffixed with.
 * Both older producers are reachable — the generator names its pieces after
 * the PLACEMENT (`vent_hood_range`) and the run after the OBJECT
 * (`vent_hood_range_range_2438`).
 */
/**
 * The id of the appliance whose END PANEL this object is, or null.
 *
 * `installFor` is the field every producer stamps since 2026-09-08. The second
 * arm reads the LEGACY island panel, which predates it and is sitting in every
 * scan already saved: `buildIslandWithSink` named it
 * `island_dwEndPanel_<stamp>_<pos|neg>` beside a dishwasher named
 * `island_dishwasher_<stamp>`, so the shared stamp ties them. It costs nothing
 * once the data regenerates, and without it the panel a derived run replaced
 * stays on the island forever — two panels in one ½" slot, which is what
 * Golden West / Scan 1 had.
 */
function endPanelApplianceId(obj, appliances) {
  if (obj?.installFor) {
    return appliances.some((a) => a?.id === obj.installFor)
      ? obj.installFor
      : null;
  }
  const m = /^island_dwEndPanel_(.+)_(?:pos|neg)$/.exec(obj?.id || '');
  if (!m) return null;
  const dwId = `island_dishwasher_${m[1]}`;
  return appliances.some((a) => a?.id === dwId) ? dwId : null;
}

function isInstallPieceOf(obj, appliances) {
  const g = obj?.fixtureGroupId || '';
  const id = obj?.id || '';
  if (endPanelApplianceId(obj, appliances)) return true;
  if (obj?.installFor) return false;
  const qualifies = INSTALL_TYPES.has(obj?.type) || INSTALL_GROUP_RE.test(g);
  if (!qualifies) return false;
  for (const a of appliances) {
    if (isScanSurroundOf(obj, a)) return true;
    for (const key of installKeysFor(a)) {
      if (g.endsWith(`_${key}`) || id.endsWith(`_${key}`)) return true;
    }
  }
  return false;
}

/**
 * The surround PROCESSING builds around a scanned fridge: `<fridge id>-surround`,
 * in no group (`buildFridgeSurrounds`, post-processing/kitchen.js) — the third
 * producer, beside the generator's and the run's. Unread, an applied option #1
 * (an auto run, so it rebuilds its install) kept it beside the surround its
 * first edit built, two on one fridge.
 */
function isScanSurroundOf(obj, appliance) {
  return (
    obj?.type === 'fridge-surround' &&
    !!appliance?.id &&
    obj.id === `${appliance.id}-surround`
  );
}

/**
 * The fill's `appliancePlacements` split into the INSTALL pieces (a fridge
 * surround, an over-fridge cabinet, a range hood) and everything else.
 *
 * The test is the fixture GROUP, not "whatever is not an appliance": that list
 * also carries a sink bay's basin and faucet, which a derived run must never
 * re-emit — the generator owns them, and re-emitting duplicated the sink.
 */
function splitAppliancePlacements(placements) {
  const install = [];
  const other = [];
  for (const o of placements || []) {
    if (
      INSTALL_TYPES.has(o?.type) ||
      (o?.fixtureGroupId && INSTALL_GROUP_RE.test(o.fixtureGroupId))
    )
      install.push(o);
    else other.push(o);
  }
  return { install, other };
}

/**
 * Incumbent install pieces the fill did NOT re-emit — the old surround under a
 * different id, and an over-fridge cabinet that no longer fits above a taller
 * fridge (`buildFridgeInstall` drops it below 0.05 m, and nothing else would
 * ever delete it). Returns ids only; the caller does the removing.
 */
function staleInstallIds(existingObjects, run, freshObjects) {
  const applianceIds = applianceObjectIds(run);
  const appliances = (existingObjects || []).filter(
    (o) => o?.id && applianceIds.has(o.id)
  );
  if (!appliances.length) return [];
  const fresh = new Set((freshObjects || []).map((o) => o?.id).filter(Boolean));
  // Which appliances this solve actually built an `installFor` piece for. A
  // piece that names its appliance is swept only when a REPLACEMENT for that
  // same appliance exists: an end panel is the one thing standing between a
  // dishwasher's bare side and the room, and a solve that emitted none (the
  // run found no room beside it) must not leave the terminus open.
  const replaced = new Set(
    (freshObjects || []).map((o) => o?.installFor).filter(Boolean)
  );
  return (existingObjects || [])
    .filter((o) => o?.id && !fresh.has(o.id) && isInstallPieceOf(o, appliances))
    .filter((o) => {
      // A hood or a surround is swept on sight — #1754: the stale one is the
      // 31" surround still wrapped around a 50" fridge. An END PANEL is not:
      // it is the only thing between a dishwasher's bare side and the room,
      // so it goes only when this solve built its replacement.
      const ep = endPanelApplianceId(o, appliances);
      return !ep || replaced.has(ep);
    })
    .map((o) => o.id);
}

/**
 * The incumbent an install piece is replacing: by id, else the same TYPE
 * inside the appliance's install group. The id fallback is load-bearing on
 * the FIRST rebuild — the generator names its surround after the placement
 * (`fridge_surround_fridge`) and the run names it after the object
 * (`fridge_surround_<objId>`), so an id match alone would miss it and the
 * product the user chose would be silently replaced exactly once.
 */
function incumbentInstallPiece(built, existingObjects, appliances) {
  if (!built?.id) return null;
  const list = existingObjects || [];
  return (
    list.find((o) => o?.id === built.id) ||
    list.find(
      (o) => o?.type === built.type && isInstallPieceOf(o, appliances)
    ) ||
    null
  );
}

/**
 * Re-emit a rebuilt install piece OVER its incumbent.
 *
 * The run owns where the piece SITS; it does not own the product standing
 * there. `{...prior, ...built}` keeps the incumbent's model, finish and
 * customization for free — `built` carries no such keys — so a hood the user
 * gave a catalog GLB is no longer reverted to the procedural placeholder by
 * the next re-solve. That is the same revert #1754 fixed for the appliance
 * object itself (`buildApplianceObjects`' reuse), one level out.
 *
 * SIZE is the other half. A placeholder is the builder's to size, and so is a
 * product whose doc claims `isScalable`; any other chosen product keeps its
 * own dimensions, because writing the bay's width onto it stretches the mesh
 * (`mayResizeObject`). Such a piece is only RE-SEATED: the rebuild's
 * along-wall centre is taken, while its distance from the wall and its
 * bottom edge are corrected back to the product's own depth and height —
 * otherwise a 24"-deep hood would hang at a 12" hood's offset. A piece the
 * USER sized keeps the axes they set and is re-seated the same way; the
 * builder sizes the rest (`ownSizeAxes`). A Venus wood hood (`hoodSku`) is
 * sized like a product — the SKU's box — while its line sells it
 * (`woodHoods`); on any other line it reverts to the built hood.
 *
 * @param {{ woodHoods?: boolean }} [opts] whether the run's line sells wood
 *   hoods (`woodHoodUnits`)
 * @returns {{piece, resized: boolean, userSized: boolean,
 *   droppedHoodSku?: string}} `resized` when the width is the builder's;
 *   `userSized` when a size the user set was kept; `droppedHoodSku` the wood
 *   hood's SKU when its line sells none and it was turned back to metal.
 */
function reuseInstallPiece(
  built,
  existingObjects,
  appliances,
  wall,
  { woodHoods = false } = {}
) {
  const incumbent = incumbentInstallPiece(built, existingObjects, appliances);
  if (!incumbent) return { piece: built, resized: true, userSized: false };
  // A wood hood on a run whose line sells none (the run left Venus) is the
  // built metal hood again: its wood build goes, the builder sizes it, and
  // the caller says why (`droppedHoodSku`).
  const dropped = isWoodHood(incumbent) && !woodHoods;
  const prior = dropped ? withoutWoodHood(incumbent) : incumbent;
  const merged = { ...prior, ...built };
  const own = ownSizeAxes(prior);
  const droppedHoodSku = dropped ? { droppedHoodSku: incumbent.hoodSku } : {};
  if (!own.length)
    return {
      piece: merged,
      resized: true,
      userSized: false,
      ...droppedHoodSku,
    };

  const userSized = mayResizeObject(prior) && !isWoodHood(prior);
  // Bottom edge stays where the rebuild put it (a hood's gap above the
  // cooktop, a surround's floor), the piece supplies the height.
  const bottom = (built.center?.y ?? 0) - (built.dimensions?.y || 0) / 2;
  const dims = userSized
    ? userSizedDimensions(built, prior, own, bottom)
    : ownDimensions(prior) || built.dimensions;
  const center = { ...built.center };
  center.y = bottom + (dims?.y || 0) / 2;
  // Keep the piece flush the way the rebuild intended: the built centre is
  // pushed off the wall by half the BUILT depth.
  const axes = wall ? getWallAxes(wall) : null;
  const dz = ((built.dimensions?.z || 0) - (dims?.z || 0)) / 2;
  if (axes && Math.abs(dz) > 1e-9) {
    center.x -= dz * axes.wallNormal.x;
    center.z -= dz * axes.wallNormal.z;
  }
  return {
    piece: { ...merged, dimensions: dims, center },
    resized: !own.includes('x'),
    userSized,
    ...droppedHoodSku,
  };
}

const SIZE_AXES = ['x', 'y', 'z'];

/**
 * A piece's own size, kept whole. A wood hood's is its SKU's and nothing
 * more: the metal hood's duct cover (`chimneyHeight`) is not drawn for it —
 * the wall above it under a stacked row is left open.
 */
function ownDimensions(piece) {
  if (!piece?.dimensions) return null;
  if (!isWoodHood(piece)) return piece.dimensions;
  const { chimneyHeight: _cover, ...dims } = piece.dimensions;
  return dims;
}

/**
 * The axes of an install piece's size that are its OWN, kept by a re-solve,
 * rather than the builder's.
 *
 * A chosen product the run may not stretch owns all three
 * (`mayResizeObject`). Any other piece owns the axes the USER set in a size
 * editor, which `recordInstallPieceSize` (applianceChange.js) records as
 * `userSizedAxes`: a hood made taller keeps its height through the next bay
 * edit, and still widens over a wider range, because its width was never the
 * user's. A placeholder nobody resized owns none.
 */
function ownSizeAxes(piece) {
  // a wood hood is its SKU's size, like a product the run may not stretch
  if (!mayResizeObject(piece) || isWoodHood(piece)) return SIZE_AXES;
  const set = new Set(
    Array.isArray(piece?.userSizedAxes) ? piece.userSizedAxes : []
  );
  return SIZE_AXES.filter((a) => set.has(a));
}

/**
 * A user-sized piece's dimensions on the rebuild: the builder's, with the
 * user's value on each axis they set. A hood's chimney still reaches the
 * ceiling the builder measured to (`resizeDimensions`).
 */
function userSizedDimensions(built, prior, own, bottom) {
  const kept = {};
  for (const a of own) {
    const v = prior.dimensions?.[a];
    if (Number.isFinite(v)) kept[a] = v;
  }
  const height = kept.y ?? built.dimensions?.y ?? 0;
  return resizeDimensions(built.dimensions, kept, {
    previousCenterY: built.center?.y,
    nextCenterY: bottom + height / 2,
  });
}

/** Bay kinds whose install piece HANGS in the upper band: a range hood. */
const HOOD_BAY_KINDS = new Set(['range', 'cooktop']);

/** The hood standing over `appliance`, if any. */
function hoodFor(appliance, objects) {
  return (
    (objects || []).find(
      (o) => o?.type === 'vent-hood' && isInstallPieceOf(o, [appliance])
    ) || null
  );
}

/**
 * Param spans a range hood claims in the UPPER band, for the hoods that are
 * WIDER than the bay below them.
 *
 * `fillUppers` hangs no upper over a range or cooktop bay — "the hood hangs
 * here" — so a hood built at the bay's width needs nothing more. A chosen
 * PRODUCT keeps its own size though (`mayResizeObject`), and so does a width
 * the user set (`ownSizeAxes`), and a 48" hood over a 36" range overhangs
 * into the neighbouring bays, where uppers do hang. So
 * reserve the hood's real footprint and let the packer trim its neighbours,
 * exactly as it trims them around a window.
 *
 * Derived from the SOLVED lower bays, not the stored anchors: the packer may
 * have clamped the bay away from a corner, and the hood hangs over where the
 * range actually ended up.
 *
 * A Venus wood hood's footprint takes in its moldings, which stand proud of
 * its sides (`woodHoods.woodHoodSideProudM`), and says where its box is
 * (`box`), so a filler can run from its side to the uppers beside it
 * (`hoodClearance.js`).
 *
 * @param {Object} run rehydrated run
 * @param {Object[]} objects the room's objects
 * @param {{segIndex, start, end, kind, bayId}[]} lowerBays solved lower bays
 * @returns {{segIndex, lo, hi, hoodId, bayId, box?: {lo, hi}}[]}
 */
function hoodSkySpans(run, objects, lowerBays) {
  const out = [];
  if (!Array.isArray(objects) || !objects.length) return out;
  const bayById = new Map(baysForRun(run).map((b) => [b.id, b]));
  for (const lb of lowerBays || []) {
    if (!HOOD_BAY_KINDS.has(lb?.kind)) continue;
    const bay = lb.bayId ? bayById.get(lb.bayId) : null;
    if (!bay) continue;
    const appliance = applianceObjectFor(bay, objects);
    if (!appliance) continue;
    const hood = hoodFor(appliance, objects);
    // Only a hood whose WIDTH is its own — a product the run may not resize,
    // or one the user widened — can differ from the bay; anything else is
    // rebuilt at the bay's width and already fits the hole.
    if (!hood || !ownSizeAxes(hood).includes('x')) continue;
    // a wood hood's moldings stand proud of its sides: the uppers stand clear
    // of them too, a filler between (the user, 2026-11-01: cut flush they
    // "dive into the front of the upper cabinet doors"; `hoodClearance.js`)
    const proud = isWoodHood(hood) ? woodHoodSideProudM(hood) : 0;
    const box = hood.dimensions?.x || 0;
    const width = box + 2 * proud;
    const own = lb.end - lb.start;
    if (!(width > own + 1e-6)) continue;
    const centre = (lb.start + lb.end) / 2;
    out.push({
      segIndex: lb.segIndex,
      lo: centre - width / 2,
      hi: centre + width / 2,
      hoodId: hood.id,
      bayId: bay.id,
      // where the wood hood's box stands, for the filler out to its side
      ...(proud > 0
        ? { box: { lo: centre - box / 2, hi: centre + box / 2 } }
        : {}),
    });
  }
  return out;
}

module.exports = {
  endPanelApplianceId,
  INSTALL_GROUP_RE,
  HOOD_BAY_KINDS,
  hoodFor,
  hoodSkySpans,
  INSTALL_TYPES,
  incumbentInstallPiece,
  ownSizeAxes,
  reuseInstallPiece,
  applianceObjectIds,
  installKeysFor,
  isInstallPieceOf,
  splitAppliancePlacements,
  staleInstallIds,
};
